import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import type { WithdrawalRepository } from './repository';
import type { WithdrawalCursorQuery, WithdrawalListQuery } from './types';

type WithdrawalListResponse = ApiResponse<'/api/v1/affiliate-withdrawals', 'get'>;
type WithdrawalDetailResponse = ApiResponse<'/api/v1/affiliate-withdrawals/{id}', 'get'>;
type WithdrawalEventsResponse = ApiResponse<'/api/v1/affiliate-withdrawals/{id}/events', 'get'>;
type WithdrawalAttemptsResponse = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/delivery-attempts',
  'get'
>;
type WithdrawalRetryResponse = ApiResponse<'/api/v1/affiliate-withdrawals/{id}/retry', 'post'>;
type WithdrawalResolutionPreviewResponse = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/resolutions/preview',
  'post'
>;
type WithdrawalResolutionResponse = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/resolutions',
  'post'
>;

export function createRealWithdrawalRepository(
  client: ApiClient,
  accessToken: string | null,
): WithdrawalRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const response = await client.get<WithdrawalListResponse>('/api/v1/affiliate-withdrawals', {
        ...auth,
        query: withdrawalListQuery(query),
        signal,
      });
      return page(response);
    },
    async get(withdrawalId, signal) {
      const response = await client.get<WithdrawalDetailResponse>(withdrawalPath(withdrawalId), {
        ...auth,
        signal,
      });
      return response.data.data;
    },
    async listEvents(withdrawalId, query, signal) {
      const response = await client.get<WithdrawalEventsResponse>(
        `${withdrawalPath(withdrawalId)}/events`,
        { ...auth, query: cursorQuery(query), signal },
      );
      return page(response);
    },
    async listDeliveryAttempts(withdrawalId, query, signal) {
      const response = await client.get<WithdrawalAttemptsResponse>(
        `${withdrawalPath(withdrawalId)}/delivery-attempts`,
        { ...auth, query: cursorQuery(query), signal },
      );
      return page(response);
    },
    async retry(withdrawalId, input, idempotencyKey) {
      const response = await client.post<WithdrawalRetryResponse>(
        `${withdrawalPath(withdrawalId)}/retry`,
        input,
        { ...auth, idempotencyKey: requireUuid(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
    async previewResolution(withdrawalId, input) {
      const response = await client.post<WithdrawalResolutionPreviewResponse>(
        `${withdrawalPath(withdrawalId)}/resolutions/preview`,
        input,
        auth,
      );
      return response.data.data;
    },
    async createResolution(withdrawalId, input, idempotencyKey) {
      const response = await client.post<WithdrawalResolutionResponse>(
        `${withdrawalPath(withdrawalId)}/resolutions`,
        input,
        { ...auth, idempotencyKey: requireUuid(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
  };
}

function withdrawalListQuery(query: WithdrawalListQuery): Readonly<Record<string, unknown>> {
  const filters = query.filters;
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    sort: query.sort,
    partnerId: filters.partnerId.trim(),
    agentId: filters.agentId.trim(),
    walletId: filters.walletId.trim(),
    memberId: filters.memberId.trim(),
    username: filters.username.trim(),
    partnerUserId: filters.partnerUserId.trim(),
    partnerWithdrawalId: filters.partnerWithdrawalId.trim(),
    transferId: filters.transferId.trim(),
    partnerCreditId: filters.partnerCreditId.trim(),
    currency: filters.currency.trim().toUpperCase(),
    status: filters.status,
    failureCode: filters.failureCode.trim(),
    deliveryResult: filters.deliveryResult,
    minimumAmount: filters.minimumAmount.trim(),
    maximumAmount: filters.maximumAmount.trim(),
    createdFrom: bangkokWallTimeToRfc3339(filters.createdFrom),
    createdTo: bangkokWallTimeToRfc3339(filters.createdTo),
    completedFrom: bangkokWallTimeToRfc3339(filters.completedFrom),
    completedTo: bangkokWallTimeToRfc3339(filters.completedTo),
  });
}

function cursorQuery(query: WithdrawalCursorQuery) {
  return compact({ cursor: query.cursor, limit: query.pageSize });
}

function page<Item>(response: {
  readonly data: {
    readonly data: readonly Item[];
    readonly pagination: { readonly nextCursor: string | null };
  };
  readonly requestId: string | null;
}) {
  return {
    items: response.data.data,
    nextCursor: response.data.pagination.nextCursor,
    requestId: response.requestId,
  };
}

function compact(values: Readonly<Record<string, unknown>>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function withdrawalPath(withdrawalId: string) {
  return `/api/v1/affiliate-withdrawals/${encodeURIComponent(withdrawalId)}`;
}

function requireUuid(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)) {
    throw new Error('A UUID v4 idempotency key is required');
  }
  return value;
}
